import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote } from 'lucide-react';
import { PageLayout, FormLayout, FormField } from '../common/index.js';

const EditTestimonial = () => {
  const { testimonialId } = useParams();
  const [formData, setFormData] = useState({
    full_name: '',
    location: '',
    rating: '5',
    testimonial_text: '',
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageBase64, setExistingImageBase64] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonial();
  }, [testimonialId]);

  const fetchTestimonial = async () => {
    try {
      const response = await testimonialService.getTestimonial(testimonialId);
      const data = response.data.data;
      setFormData({
        full_name: data.full_name,
        location: data.location,
        rating: data.rating.toString(),
        testimonial_text: data.testimonial_text,
        is_active: data.is_active,
      });
      if (data.image_base64) setExistingImageBase64(data.image_base64);
    } catch (err) {
      Alert.error('Failed to load testimonial data');
      navigate('/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1048576) {
      Alert.error('Image size must be less than 1MB');
      e.target.value = '';
      return;
    }
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      Alert.error('Only PNG, JPG, JPEG, GIF, and WebP images are allowed');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setRemoveImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setExistingImageBase64(null);
    setRemoveImage(true);
    const imageInput = document.querySelector('input[name="image"]');
    if (imageInput) imageInput.value = '';
  };

  const getDisplayImage = () => {
    if (imagePreview) return imagePreview;
    if (existingImageBase64) return `data:image/jpeg;base64,${existingImageBase64}`;
    return null;
  };

  const handleSubmit = async (e) => {
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('full_name', formData.full_name);
      form.append('location', formData.location);
      form.append('rating', formData.rating);
      form.append('testimonial_text', formData.testimonial_text);
      form.append('is_active', formData.is_active);
      if (e.target.image.files[0]) {
        form.append('image', e.target.image.files[0]);
      } else if (removeImage) {
        form.append('remove_image', 'true');
      }
      await testimonialService.updateTestimonial(testimonialId, form);
      Alert.success('Testimonial updated successfully!');
      setTimeout(() => navigate('/testimonials'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Testimonial" icon={MessageSquareQuote}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
        </div>
      </PageLayout>
    );
  }

  const displayImage = getDisplayImage();

  return (
    <FormLayout
      title="Edit Testimonial"
      icon={MessageSquareQuote}
      onBack={() => navigate('/testimonials')}
      onSubmit={handleSubmit}
      submitLabel={submitting ? 'Updating...' : 'Update Testimonial'}
      onCancel={() => navigate('/testimonials')}
      loading={submitting}
    >
      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
          Customer Information
        </h3>
        <div className="space-y-4">
          <FormField label="Full Name" required>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} required className="input-clean w-full" />
          </FormField>
          <FormField label="Location" required>
            <input type="text" name="location" value={formData.location} onChange={handleChange} required className="input-clean w-full" />
          </FormField>
          <FormField label="Customer Image (Optional)">
            {displayImage && !imageError && (
              <div className="mb-3 relative inline-block">
                <img src={displayImage} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2 border-gray-200" onError={() => setImageError(true)} />
                <button type="button" onClick={handleRemoveImage} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs">×</button>
              </div>
            )}
            <input type="file" name="image" accept="image/*" onChange={handleImageChange} className="input-clean w-full" />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
          Testimonial Details
        </h3>
        <div className="space-y-4">
          <FormField label="Rating" required>
            <select name="rating" value={formData.rating} onChange={handleChange} className="input-clean w-full">
              <option value="5">5 ⭐⭐⭐⭐⭐</option>
              <option value="4">4 ⭐⭐⭐⭐</option>
              <option value="3">3 ⭐⭐⭐</option>
              <option value="2">2 ⭐⭐</option>
              <option value="1">1 ⭐</option>
            </select>
          </FormField>
          <FormField label="Testimonial Text" required>
            <textarea name="testimonial_text" value={formData.testimonial_text} onChange={handleChange} rows={5} required className="input-clean w-full resize-none" />
          </FormField>
          <FormField label="Active Status" required>
            <select name="is_active" value={formData.is_active} onChange={handleChange} className="input-clean w-full">
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
          </FormField>
        </div>
      </div>
    </FormLayout>
  );
};

export default EditTestimonial;
